import { useMemo, useState, type ReactNode } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useDeleteLocation } from 'hooks/icsLocation/useDeleteLocation';
import {
  fetchInternalLayerVersions,
  useListInternalLayerVersionsQuery,
} from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { useListTakEntitiesQuery } from 'hooks/takEntity/useListTakEntitiesQuery';
import type { TakEntity } from 'hooks/takEntity/types';
import { usePublishInternalLayerMutation } from 'hooks/internalLayer/usePublishInternalLayerMutation';
import { useApproveInternalLayerMutation } from 'hooks/internalLayer/useApproveInternalLayerMutation';
import { useDeleteInternalLayerMutation } from 'hooks/internalLayer/useDeleteInternalLayerMutation';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import { LayerFeaturesContent } from '../LayersTab/LayerFeaturesContent';
import { LayerMetadata } from '../LayersTab/LayerDataRows';
import { FeatureDetailRows, Layer, PropertyRow } from '../LayersTab/Layer';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import {
  canPublishInternalLayer,
  InternalLayerVersionBanner,
} from '../LayersTab/InternalLayerVersionBanner';
import { LayerPermissionsDialog } from '../LayersTab/LayerPermissionsDialog';
import { PublishToOrganizationDialog } from '../LayersTab/PublishToOrganizationDialog';
import { resolveLayerProvenance } from '../LayersTab/utils/layerProvenance';
import {
  EriLayerBody,
  PowerOutageLayerBody,
} from '../LayersTab/OutageLayerBodies';
import {
  ERI_LAYER_TITLE,
  POWER_OUTAGE_LAYER_TITLE,
  useEriLayerControls,
  usePowerOutageLayerControls,
} from '../LayersTab/useOutageLayerControls';
import { SitStatFeatureItem } from './SitStatFeatureItem';
import { SitStatLayerListShell } from './SitStatLayerListShell';
import { useWorkspaceName } from '../LayersTab/useWorkspaceName';
import {
  InternalLayerDetailsFields,
  InternalLayerNameEditor,
} from '../LayersTab/InternalLayerDetailsFields';

function SitStatInternalLayerBody({ layer }: { layer: InternalLayer }) {
  const { getSelectedVersionForLayer, setSelectedInternalLayerVersion } =
    useUscgMapContext();
  const activeVersionId = getSelectedVersionForLayer(layer.id)?.id;
  const deleteLocation = useDeleteLocation();

  const handleDeleteLocation = async (objectId: string) => {
    const response = await deleteLocation.mutateAsync(objectId);
    const versionId = response.data.internalLayerVersionId;
    if (!versionId) return;
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: layer.id },
    });
    const match = versions.data.find((version) => version.id === versionId);
    if (match) setSelectedInternalLayerVersion(layer.id, match);
  };

  const { data } = useListInternalLayerVersionsQuery({
    filter: {
      internalLayerId: layer.id,
    },
  });
  const versions = useMemo(() => data?.data ?? [], [data?.data]);
  const activeVersion = versions.find((v) => v.id === activeVersionId);
  const canChangeDraft =
    layer.publishedAt != null || Boolean(layer.viewerCanEdit);
  const canEditVersion =
    activeVersion?.approval?.approvalStatus === 'DRAFT' && canChangeDraft;

  return (
    <>
      <InternalLayerVersionBanner layer={layer} />
      <InternalLayerDetailsFields
        key={`${layer.id}:${layer.updatedAt}`}
        layer={layer}
      />
      <LayerFeaturesContent
        layer={layer}
        layerType="internal"
        showAddObjectButton={canChangeDraft}
        onDelete={canEditVersion ? handleDeleteLocation : undefined}
        FeatureItemComponent={SitStatFeatureItem}
      />
    </>
  );
}

export function SitStatInternalLayerItem({
  layer,
  onZoomToLayer,
  onFocusView,
  defaultOpen = false,
  dataTour,
  zoomTour,
}: {
  layer: InternalLayer;
  onZoomToLayer?: () => void;
  onFocusView?: () => void;
  defaultOpen?: boolean;
  dataTour?: string;
  zoomTour?: string;
}) {
  const { isInternalLayerVisible, toggleInternalLayerVisibility } =
    useUscgMapContext();
  const userGuid = useAppSelector(selectUserGuid);
  const publishLayer = usePublishInternalLayerMutation();
  const approveLayer = useApproveInternalLayerMutation();
  const archiveLayer = useDeleteInternalLayerMutation();
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const showPublish = canPublishInternalLayer(layer, userGuid);
  const publishToOrganization = Boolean(layer.incidentId);
  const workspaceName = useWorkspaceName(
    layer.incidentId,
    layer.incident?.name
  );

  return (
    <>
      <SitStatLayerListShell
        title={layer.name || 'Untitled layer'}
        nameEditor={<InternalLayerNameEditor layer={layer} />}
        workspaceName={workspaceName}
        checked={isInternalLayerVisible(layer.id)}
        onCheckedChange={() => toggleInternalLayerVisibility(layer.id)}
        onZoom={onZoomToLayer}
        onFocusView={onFocusView}
        onPermissions={() => setPermissionsOpen(true)}
        defaultOpen={defaultOpen}
        dataTour={dataTour}
        zoomTour={zoomTour}
        onPublish={
          showPublish
            ? publishToOrganization
              ? () => setPublishOpen(true)
              : () => publishLayer.mutate({ internalLayerId: layer.id })
            : undefined
        }
        publishLabel={
          publishToOrganization ? 'Publish to Organization' : 'Publish'
        }
        publishDisabled={publishLayer.isPending}
        onApprove={
          layer.incidentId && !layer.approvedAt
            ? () => approveLayer.mutate({ internalLayerId: layer.id })
            : undefined
        }
        onArchive={() => archiveLayer.mutate({ internalLayerId: layer.id })}
      >
        <SitStatInternalLayerBody layer={layer} />
      </SitStatLayerListShell>
      <LayerPermissionsDialog
        layer={layer}
        open={permissionsOpen}
        onClose={() => setPermissionsOpen(false)}
      />
      {publishToOrganization ? (
        <PublishToOrganizationDialog
          layer={layer}
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
        />
      ) : null}
    </>
  );
}

export function SitStatExternalLayerItem({
  layer,
  onOpenInNewClick,
  onZoomToLayer,
  onZoomToGraphic,
  dataTour,
  zoomTour,
  qualityTour,
}: {
  layer: ExternalLayer;
  onOpenInNewClick?: () => void;
  onZoomToLayer?: () => void;
  onZoomToGraphic: (graphic: __esri.Graphic) => void;
  dataTour?: string;
  zoomTour?: string;
  qualityTour?: string;
}) {
  const {
    isExternalLayerVisible,
    isExternalLayerLoadError,
    toggleExternalLayerVisibility,
  } = useUscgMapContext();
  const hasLoadError = isExternalLayerLoadError(layer.id);
  const provenance = resolveLayerProvenance(layer.serviceUrl);

  return (
    <SitStatLayerListShell
      title={layer.name || 'Untitled layer'}
      checked={isExternalLayerVisible(layer.id)}
      onCheckedChange={() => toggleExternalLayerVisibility(layer.id)}
      showArcGisBadge
      provenanceBadge={provenance.badge}
      error={
        hasLoadError
          ? `Failed to load layer from ${provenance.source}. Please check your permissions. (itemId: ${layer.arcgisItemId})`
          : undefined
      }
      onZoom={onZoomToLayer}
      onViewDetails={onOpenInNewClick}
      dataTour={dataTour}
      zoomTour={zoomTour}
      qualityTour={qualityTour}
    >
      <LayerMetadata
        lastUpdated={formatDateTime(layer.updatedAt)}
        aorIds={layer.aorIds}
        incident={layer.incidentId}
        sources={provenance.source}
      />
      <LayerFeaturesContent
        layer={layer}
        layerType="external"
        onZoomToExternalGraphic={onZoomToGraphic}
        FeatureItemComponent={SitStatFeatureItem}
      />
    </SitStatLayerListShell>
  );
}

function entityTitle(entity: TakEntity): string {
  return entity.callsign?.trim() || entity.uid;
}

function entityPointGraphic(entity: TakEntity): Graphic {
  return new Graphic({
    geometry: new Point({ longitude: entity.lon, latitude: entity.lat }),
    attributes: {
      takUid: entity.uid,
      name: entityTitle(entity),
      category: entity.category,
      cotType: entity.cotType,
    },
  });
}

export function SitStatTakLayerItem() {
  const { mapView, isTakVisible, toggleTakVisibility, focusFeatureOnMap } =
    useUscgMapContext();

  const { data, isLoading } = useListTakEntitiesQuery({
    enabled: true,
  });
  const entities = data?.data ?? [];
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entities;
    return entities.filter((entity) => {
      const haystack = [
        entity.callsign,
        entity.uid,
        entity.category,
        entity.cotType,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [entities, searchTerm]);

  const zoomToLayer = async () => {
    if (!mapView || entities.length === 0) return;
    if (!isTakVisible) toggleTakVisibility();
    const graphics = entities.map(entityPointGraphic);
    await mapView.goTo(graphics, { duration: 500 });
  };

  const zoomToEntity = async (entity: TakEntity) => {
    if (!isTakVisible) toggleTakVisibility();
    await focusFeatureOnMap({ graphic: entityPointGraphic(entity) });
  };

  return (
    <SitStatLayerListShell
      title="TAK (Team Awareness Kit)"
      checked={isTakVisible}
      onCheckedChange={() => toggleTakVisibility()}
      onZoom={() => {
        void zoomToLayer();
      }}
      zoomDisabled={entities.length === 0}
      zoomLabel="Zoom to TAK entities"
    >
      <PropertyRow label="Entities" value={String(entities.length)} />
      <Layer.FeaturesList
        isLoading={isLoading}
        searchTerm={searchTerm}
        onChangeSearchTerm={setSearchTerm}
        showSearch
        showAddObjectButton={false}
      >
        {filtered.length === 0 ? (
          <p className="small text-muted mb-0 p-2 pt-0">
            {entities.length === 0
              ? 'No TAK entities available.'
              : 'No entities match this search.'}
          </p>
        ) : (
          <ul className="list-unstyled d-grid mb-0 gap-2">
            {filtered.map((entity) => (
              <SitStatFeatureItem
                key={entity.uid}
                title={entityTitle(entity)}
                externalFeature={{ featureId: entity.uid }}
                onZoom={() => zoomToEntity(entity)}
              >
                <FeatureDetailRows
                  lastUpdated={formatDateTime(entity.updatedAt)}
                  point={{
                    latitude: entity.lat,
                    longitude: entity.lon,
                  }}
                />
                <PropertyRow label="Category" value={entity.category} />
                <PropertyRow label="CoT type" value={entity.cotType} />
                <PropertyRow label="UID" value={entity.uid} />
              </SitStatFeatureItem>
            ))}
          </ul>
        )}
      </Layer.FeaturesList>
    </SitStatLayerListShell>
  );
}

/**
 * SitStat row for the county-level "Power Outages" choropleth (POWER_OUTAGES
 * flag).
 */
export function SitStatPowerOutageLayerItem() {
  const controls = usePowerOutageLayerControls();

  return (
    <SitStatLayerListShell
      title={POWER_OUTAGE_LAYER_TITLE}
      checked={controls.isVisible}
      onCheckedChange={() => controls.toggleVisibility()}
      onZoom={controls.zoomToLayer}
      zoomDisabled={controls.zoomDisabled}
      zoomLabel="Zoom to power outages"
      provenanceBadge="PowerOutage.us"
      showArcGisBadge
    >
      <PowerOutageLayerBody controls={controls} />
    </SitStatLayerListShell>
  );
}

/**
 * SitStat row for the county-level "Outage Prediction (ERI)" choropleth
 * (POWER_OUTAGES flag).
 */
export function SitStatEriLayerItem() {
  const controls = useEriLayerControls();

  return (
    <SitStatLayerListShell
      title={ERI_LAYER_TITLE}
      checked={controls.isVisible}
      onCheckedChange={() => controls.toggleVisibility()}
      onZoom={controls.zoomToLayer}
      zoomDisabled={controls.zoomDisabled}
      zoomLabel="Zoom to ERI forecast"
      provenanceBadge="ERI"
      showArcGisBadge
    >
      <EriLayerBody controls={controls} />
    </SitStatLayerListShell>
  );
}

export function SitStatLayerFolderRow({
  label,
  checked,
  indeterminate,
  onCheckedChange,
  defaultOpen = true,
  onViewDetails,
  dataTour,
  children,
}: {
  label: string;
  checked: boolean;
  indeterminate: boolean;
  onCheckedChange: () => void;
  defaultOpen?: boolean;
  onViewDetails?: () => void;
  /** `data-tour` on the folder row for product tours. */
  dataTour?: string;
  children: ReactNode;
}) {
  return (
    <SitStatLayerListShell
      title={label}
      checked={checked}
      indeterminate={indeterminate}
      onCheckedChange={() => onCheckedChange()}
      defaultOpen={defaultOpen}
      onViewDetails={onViewDetails}
      dataTour={dataTour}
      contentClassName="tw:space-y-2"
    >
      {children}
    </SitStatLayerListShell>
  );
}
